import React, { useState, useEffect } from 'react';
import { Search, Check, Upload, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { getMediaBankImages } from '../firebase/services';

const ImagePicker = ({ isOpen, onClose, onSelect, selectedUrl = null, multiple = false }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadImages();
      if (selectedUrl && !multiple) {
        setSelectedImages([selectedUrl]);
      }
    }
  }, [isOpen, selectedUrl, multiple]);

  const loadImages = async () => {
    try {
      setLoading(true);
      const allImages = await getMediaBankImages(searchTerm, 1000);
      setImages(allImages);
    } catch (error) {
      console.error('Error loading images:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        loadImages();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchTerm]);

  const handleImageClick = (imageUrl) => {
    if (multiple) {
      setSelectedImages(prev =>
        prev.includes(imageUrl)
          ? prev.filter(url => url !== imageUrl)
          : [...prev, imageUrl]
      );
    } else {
      setSelectedImages([imageUrl]);
    }
  };

  const handleSelect = () => {
    if (multiple) {
      onSelect(selectedImages);
    } else {
      onSelect(selectedImages[0] || null);
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Select Image from Media Bank</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <Input
              type="text"
              placeholder="Search images by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Info */}
          <div className="flex justify-between items-center text-sm text-gray-600">
            <p>{images.length} images available</p>
            {selectedImages.length > 0 && (
              <p className="text-[#4a1a1f] font-medium">
                {selectedImages.length} selected
              </p>
            )}
          </div>

          {/* Image Grid */}
          <div className="border rounded-lg overflow-auto max-h-96">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4a1a1f] mx-auto mb-4"></div>
                <p className="text-gray-600">Loading images...</p>
              </div>
            ) : images.length === 0 ? (
              <div className="text-center py-12">
                <Upload size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 mb-2">
                  {searchTerm ? 'No images found' : 'No images in media bank'}
                </p>
                <p className="text-sm text-gray-500">
                  Upload images in the Media Bank section first
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3 p-4">
                {images.map((image) => {
                  const isSelected = selectedImages.includes(image.url);
                  return (
                    <div
                      key={image.firebaseId}
                      onClick={() => handleImageClick(image.url)}
                      className={`relative cursor-pointer group border-2 rounded-lg overflow-hidden transition-all ${
                        isSelected
                          ? 'border-[#4a1a1f] ring-2 ring-[#4a1a1f] ring-offset-2'
                          : 'border-transparent hover:border-gray-300'
                      }`}
                    >
                      {/* Image */}
                      <div className="aspect-square bg-gray-100">
                        <img
                          src={image.url}
                          alt={image.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Selected Indicator */}
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-[#4a1a1f] text-white rounded-full p-1">
                          <Check size={16} />
                        </div>
                      )}

                      {/* Name Overlay */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-xs truncate" title={image.name}>
                          {image.name}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setSelectedImages([]);
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSelect}
              disabled={selectedImages.length === 0}
              className="bg-[#4a1a1f] hover:bg-[#5a2a2f]"
            >
              Select {selectedImages.length > 0 && `(${selectedImages.length})`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImagePicker;
